import { Controller } from "@hotwired/stimulus";

// SearchQueryController powers the GitHub-style query bar
// (components/query_bar.html): local autocomplete for `key:value`
// qualifiers, fed by the vocabulary the server injects as the schema
// value — [{key: "severity", values: ["critical", ...]}, ...]. It never
// builds a query itself; the filter strip's links are server-rendered
// and this controller only completes tokens in the box and keeps the
// <details> dropdowns tidy (one open at a time, closed on outside click).
export default class extends Controller {
  static targets = ["input", "list", "menu"];
  static values = { schema: Array };

  connect() {
    this.activeIndex = -1;
    this.outsideListener = (e) => {
      if (this.element.contains(e.target)) return;
      this.hide();
      this.closeMenus();
    };
    document.addEventListener("click", this.outsideListener);
    // At most one dropdown open: opening a menu closes its siblings.
    this.menuTargets.forEach((d) => {
      d.addEventListener("toggle", () => {
        if (d.open) this.menuTargets.forEach((o) => { if (o !== d) o.open = false; });
      });
    });
  }

  disconnect() {
    document.removeEventListener("click", this.outsideListener);
  }

  onInput() { this.suggest(); }
  onFocus() { this.suggest(); }

  onKeyDown(e) {
    const items = this.items();
    if (e.key === "ArrowDown") {
      e.preventDefault();
      if (items.length === 0) { this.suggest(); return; }
      this.setActive((this.activeIndex + 1) % items.length);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      if (items.length === 0) return;
      this.setActive((this.activeIndex - 1 + items.length) % items.length);
    } else if (e.key === "Enter") {
      // Only intercept when a suggestion is highlighted — a plain Enter
      // submits the form, which is the whole point of the search box.
      if (this.activeIndex >= 0 && items[this.activeIndex]) {
        e.preventDefault();
        this.pick(items[this.activeIndex].dataset.completion);
      }
    } else if (e.key === "Tab" && !e.shiftKey) {
      if (this.listTarget.classList.contains("hidden") || items.length === 0) return;
      e.preventDefault();
      this.pick(items[this.activeIndex >= 0 ? this.activeIndex : 0].dataset.completion);
    } else if (e.key === "Escape") {
      this.hide();
    }
  }

  // tokenBounds finds the whitespace-delimited token around the caret.
  // Completion works against the part before the caret, so completing in
  // the middle of a token overwrites its tail rather than mangling it.
  tokenBounds() {
    const v = this.inputTarget.value;
    const caret = this.inputTarget.selectionStart ?? v.length;
    const start = v.lastIndexOf(" ", caret - 1) + 1;
    let end = v.indexOf(" ", caret);
    if (end === -1) end = v.length;
    return { start, end, prefix: v.slice(start, caret) };
  }

  suggest() {
    const { prefix } = this.tokenBounds();
    const lower = prefix.toLowerCase();
    // A leading "-" negates the qualifier. Complete against the same
    // vocabulary and put the sign back on each completion — otherwise typing
    // "-stat" offers nothing and negation reads as unsupported. A bare "-"
    // lists every key, which is how the feature is discovered.
    const neg = lower.startsWith("-");
    const body = neg ? lower.slice(1) : lower;
    const sign = neg ? "-" : "";
    const colon = body.indexOf(":");
    let items;
    if (colon >= 0) {
      // Token already has a key — complete its values.
      const key = body.slice(0, colon);
      const rest = body.slice(colon + 1);
      const def = this.schemaValue.find((d) => d.key === key);
      items = def
        ? def.values.filter((v) => v.startsWith(rest) && v !== rest)
            .map((v) => ({ completion: sign + key + ":" + v, hint: "" }))
        : [];
    } else {
      // Complete qualifier keys, skipping ones already used elsewhere in
      // the query — a second severity: would silently lose to the first.
      // Exclusions stack instead of overwriting, so a key that is already
      // negated stays on offer; only the positive slot is single-use.
      const used = neg ? new Set() : this.usedKeys();
      items = this.schemaValue
        .filter((d) => !used.has(d.key) && (d.key + ":").startsWith(body))
        .map((d) => ({ completion: sign + d.key + ":", hint: d.values.join(" · ") }));
    }
    this.render(items, lower);
  }

  // usedKeys returns qualifier keys present in the input outside the
  // token currently being typed.
  usedKeys() {
    const { start, end } = this.tokenBounds();
    const v = this.inputTarget.value;
    const rest = v.slice(0, start) + " " + v.slice(end);
    const used = new Set();
    for (const m of rest.matchAll(/(?:^|\s)([a-zA-Z_]+):\S/g)) used.add(m[1].toLowerCase());
    return used;
  }

  render(items, lq) {
    this.listTarget.replaceChildren();
    if (items.length === 0) { this.hide(); return; }
    for (const { completion, hint } of items) {
      const li = document.createElement("li");
      li.dataset.completion = completion;
      li.className = "fa-item px-3 py-1.5 text-sm font-mono cursor-pointer flex items-center gap-2";
      let html = '<span class="truncate">' + highlightPrefix(completion, lq) + "</span>";
      if (hint) html += '<span class="truncate text-[11px] ml-auto" style="color: var(--dim);">' + escapeHTML(hint) + "</span>";
      li.innerHTML = html;
      li.addEventListener("mousedown", (e) => {
        // mousedown fires before the input's blur, so focus never leaves.
        e.preventDefault();
        this.pick(completion);
      });
      this.listTarget.appendChild(li);
    }
    this.activeIndex = -1;
    this.show();
  }

  pick(completion) {
    const el = this.inputTarget;
    const { start, end } = this.tokenBounds();
    const v = el.value;
    let insert = completion;
    const keyOnly = completion.endsWith(":");
    if (!keyOnly && !v.slice(end).startsWith(" ")) insert += " ";
    el.value = v.slice(0, start) + insert + v.slice(end);
    const caret = start + insert.length;
    el.focus();
    el.setSelectionRange(caret, caret);
    if (keyOnly) {
      this.suggest(); // straight on to the key's values
    } else {
      this.hide();
    }
  }

  closeMenus() { this.menuTargets.forEach((d) => { d.open = false; }); }

  show() { this.listTarget.classList.remove("hidden"); }
  hide() { this.listTarget.classList.add("hidden"); this.activeIndex = -1; }

  items() { return Array.from(this.listTarget.children); }
  setActive(i) {
    const items = this.items();
    items.forEach((el, idx) => el.classList.toggle("is-active", idx === i));
    this.activeIndex = i;
    if (items[i]) items[i].scrollIntoView({ block: "nearest" });
  }
}

function escapeHTML(s) {
  return String(s).replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
}

// highlightPrefix wraps the typed prefix of a completion in the same
// .fa-hit token the file autocomplete uses.
function highlightPrefix(completion, lq) {
  if (!lq || !completion.toLowerCase().startsWith(lq)) return escapeHTML(completion);
  return '<strong class="fa-hit">' + escapeHTML(completion.slice(0, lq.length)) + "</strong>" +
    escapeHTML(completion.slice(lq.length));
}
